import { Injectable } from '@angular/core';
import {
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
  ActivatedRoute,
} from '@angular/router';
import { Observable } from 'rxjs';
import * as jwt_decode from 'jwt-decode';
import { UserLoggedIn } from '../models/user-login.model';
import { CurrentUserService } from './current-user.service';
import { NotificationService } from './notification.service';
import { firstValueFrom } from 'rxjs';
import { UmRole } from 'src/app/modules/roles/models/role-enum';
import { ModuleSelectionService } from 'src/app/modules/users/services/module-selection.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private currentUserService: CurrentUserService,
    private notificationService: NotificationService,
    private moduleSelectionService: ModuleSelectionService
  ) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean> {
    let maxRetries = 10; // Maximum retry attempts
    let retryCount = 0; // Counter for retries

    return new Promise(async resolve => {
      // First, check if user exists at all - if not, no need to wait for token exchange
      let userString = localStorage.getItem(this.currentUser);
      if (!userString) {
        // No user in storage - redirect to login immediately
        this.currentUserService.setReturnUrl(state.url);
        this.router.navigate(['/login'], {
          queryParams: { returnUrl: state.url },
        });
        return resolve(false);
      }

      while (retryCount < maxRetries) {
        const isExchanged = await firstValueFrom(
          this.currentUserService.isTokenExchangeCompleted()
        );
        if (isExchanged) {
          // Token exchange is complete, proceed with original logic
          userString = localStorage.getItem(this.currentUser);
          if (!userString) {
            // Store return URL and redirect to Angular login with query param
            this.currentUserService.setReturnUrl(state.url);
            this.router.navigate(['/login'], {
              queryParams: { returnUrl: state.url },
            });
            return resolve(false);
          }

          this.User = JSON.parse(userString);
          let permissions: string[] = [];

          if (this.User) {
            let token = jwt_decode.jwtDecode(this.User.access_Token) as any;
            if (token && token.permissions) {
              permissions = token.permissions.split(',');
            }
          }

          // Check if user has a role - if not, redirect to module selection
          // This matches the MVC _Layout.cshtml validation logic
          // Skip this check if already on module selection or login pages to avoid infinite loop
          const isAuthPage =
            state.url.startsWith('/modules') ||
            state.url.startsWith('/login') ||
            state.url.startsWith('/forget-password');

          if (
            this.User &&
            !this.User.roleName &&
            !this.User.roleId &&
            !isAuthPage
          ) {
            // Store return URL and redirect to module selection with query param
            this.currentUserService.setReturnUrl(state.url);
            this.router.navigate(['/modules'], {
              queryParams: { returnUrl: state.url },
            });
            return resolve(false);
          }

          // Check if user has selected a module (for multi-module users)
          if (
            this.User &&
            this.currentUserService.hasMultipleModules() &&
            !this.currentUserService.hasSelectedModule()
          ) {
            // Store return URL and redirect to module selection with query param
            this.currentUserService.setReturnUrl(state.url);
            this.router.navigate(['/modules'], {
              queryParams: { returnUrl: state.url },
            });
            return resolve(false);
          }

          // Pages hidden from specific roles even though they hold the permission
          if (
            next.data['excludedRoleIds'] &&
            next.data['excludedRoleIds'].includes(this.User?.roleId)
          ) {
            this.router.navigateByUrl(
              this.moduleSelectionService.getModuleRedirectUrl(
                this.User!.moduleId,
                this.User!.roleId
              )
            );
            return resolve(false);
          }

          if (next.data['permissionCodes']) {
            let isGrant = next.data['permissionCodes'].some((element: any) =>
              permissions.includes(element)
            );
            if (isGrant) {
              return resolve(true);
            } else {
              // Show error notification
              // this.notificationService.showError(
              //   'You do not have permission to access this page',
              //   ''
              // );

              // Get default URL based on user's module and role
              const defaultUrl =
                this.moduleSelectionService.getModuleRedirectUrl(
                  this.User!.moduleId,
                  this.User!.roleId
                );

              // Redirect to default page
              setTimeout(() => {
                this.router.navigateByUrl(defaultUrl);
              }, 1000); // Small delay to show the error message

              return resolve(false);
            }
          }

          return resolve(true);
        }

        // If token exchange is not complete, retry after a delay
        retryCount++;
        await new Promise(res => setTimeout(res, 1000));
      }
      // If we reach max retries, fail gracefully and redirect to login with query param
      this.currentUserService.setReturnUrl(state.url);
      this.router.navigate(['/login'], {
        queryParams: { returnUrl: state.url },
      });
      return resolve(false);
    });
  }

  /**
   * Redirect to module selection page
   */
  private redirectToModuleSelection(): void {
    // Redirect to the MVC module selection page
    const returnUrl = this.currentUserService.getReturnUrl();
    const baseUrl = window.location.origin;

    if (returnUrl) {
      // Include the current Angular route as the return URL
      const fullReturnUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      window.location.href = `${baseUrl}/Account/Modules?returnUrl=${encodeURIComponent(fullReturnUrl)}`;
    } else {
      window.location.href = `${baseUrl}/Account/Modules`;
    }
  }

  showActivate(data: any): Observable<boolean> | boolean {
    let userString = localStorage.getItem(this.currentUser);

    if (userString == null || userString == 'null') {
      this.router.navigate(['/login']);
      return false;
    } else {
      if (userString != null) {
        this.User = JSON.parse(userString!);

        let permissions: [] = [];

        if (this.User) {
          let token = jwt_decode.jwtDecode(this.User!.access_Token) as any;
          //;
          if (token) {
            permissions = token.permissions.split(',');
          }
        }

        if (this.User && permissions && data?.permissionCodes) {
          let isGrant: boolean = false;
          if (data?.permissionCodes) {
            data?.permissionCodes.forEach((element: string) => {
              let permissionItem = permissions.find(x => x == element);
              if (permissionItem) {
                isGrant = true;
              }
            });
            if (isGrant) {
              return true;
            } else {
              return false;
            }
          } else {
            return false;
          }
        } else {
          return false;
        }
      } else {
        return false;
      }
    }
    return false;
  }

  HasPermission(permissionCode: any[]): Observable<boolean> | boolean {
    let userString = localStorage.getItem(this.currentUser);
    if (userString != null) {
      this.User = JSON.parse(userString!);
      let permissions: [] = [];

      if (this.User) {
        let token = jwt_decode.jwtDecode(this.User!.access_Token) as any;
        if (token) {
          permissions = token.permissions.split(',');
        }
      }

      if (this.User && permissions && permissionCode) {
        let x = permissions.find(x => x == permissionCode);
        let isGrant: boolean = false;
        permissionCode.forEach(element => {
          let permissionItem = permissions.find(x => x == element);
          if (permissionItem) {
            isGrant = true;
          }
        });
        if (isGrant) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    } else {
      return false;
    }
    return false;
  }
  hasNotPermission(permissionCode: any[]): Observable<boolean> | boolean {
    let userString = localStorage.getItem(this.currentUser);
    if (userString != null) {
      this.User = JSON.parse(userString);
      let permissions: [] = [];

      if (this.User) {
        let token = jwt_decode.jwtDecode(this.User.access_Token) as any;
        if (token) {
          permissions = token.permissions.split(',');
        }
      }

      if (this.User && permissions && permissionCode) {
        let isGrant: boolean = false;
        permissionCode.forEach(element => {
          let permissionItem = permissions.find(x => x == +element);
          if (permissionItem) {
            isGrant = true;
          }
        });
        if (isGrant) {
          return false;
        } else {
          return true;
        }
      } else {
        return true;
      }
    } else {
      return true;
    }
  }

  private currentUser: string = 'currentUser';
  User: UserLoggedIn | undefined;
}
